import { useEffect, useId, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { Plus } from "lucide-react";
import { Eyebrow, Photo, Reveal } from "./primitives";
import { applySeo } from "../../lib/seo";
import { src, srcSet } from "../../lib/img";

export const usePageMeta = (title, description) => {
  const { pathname } = useLocation();
  useEffect(() => {
    applySeo(pathname, title, description);
  }, [pathname, title, description]);
};

const TONES = {
  ivory: "bg-ivory text-ink",
  ivory50: "bg-ivory-50 text-ink",
  sand: "bg-sand text-ink",
  sandLight: "bg-sand-light text-ink",
  ocean: "bg-ocean-deep text-ivory",
  moss: "bg-moss text-ivory",
};

export const Section = ({ id, tone = "ivory", className = "", inner = "", children, testid, labelledby }) => (
  <section id={id} aria-labelledby={labelledby} data-testid={testid} className={`relative scroll-mt-24 overflow-hidden px-6 py-24 sm:px-8 lg:px-12 lg:py-32 ${TONES[tone]} ${className}`}>
    <div className={`relative mx-auto max-w-7xl ${inner}`}>{children}</div>
  </section>
);

export const Heading = ({ eyebrow, title, id, intro, dark = false, className = "", as = "h2" }) => {
  const H = as;
  return (
    <Reveal className={className}>
      {eyebrow && <Eyebrow tone={dark ? "dark" : "light"}>{eyebrow}</Eyebrow>}
      <H id={id} className="mt-5 font-display text-[2.4rem] font-light leading-[1.05] sm:text-5xl lg:text-6xl">
        {title}
      </H>
      {intro && <p className={`mt-6 max-w-2xl text-base leading-relaxed sm:text-lg ${dark ? "text-ivory/80" : "text-ink-soft"}`}>{intro}</p>}
    </Reveal>
  );
};

const enter = (reduce, d) => (reduce ? {} : { initial: { opacity: 0, y: 26 }, animate: { opacity: 1, y: 0 }, transition: { delay: d, duration: 1.2, ease: [0.22, 1, 0.36, 1] } });

export const PageHero = ({ eyebrow, title, intro, img, children, testid = "page-hero", sub }) => {
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], reduce ? ["0%", "0%"] : ["0%", "18%"]);
  return (
    <section ref={ref} data-testid={testid} className="relative flex min-h-[620px] items-end overflow-hidden bg-ink lg:h-[88vh]">
      <motion.div style={{ y }} className="absolute inset-0">
        <motion.div className="h-full w-full" initial={reduce ? false : { scale: 1.08 }} animate={{ scale: 1 }} transition={{ duration: 8, ease: "linear" }}>
          <Photo img={img} eager sizes="100vw" className="h-full w-full object-cover" />
        </motion.div>
      </motion.div>
      <div aria-hidden className="absolute inset-0 bg-[linear-gradient(to_top,rgba(28,24,20,0.85)_0%,rgba(28,24,20,0.35)_45%,rgba(28,24,20,0.1)_75%)]" />
      <div aria-hidden className="absolute inset-0 hidden bg-[linear-gradient(to_right,rgba(28,24,20,0.55)_0%,transparent_60%)] md:block" />
      <div className="relative z-10 mx-auto w-full max-w-7xl px-6 pb-16 pt-32 sm:px-8 lg:px-12 lg:pb-24">
        <motion.p {...enter(reduce, 0.15)} className="text-[11px] font-semibold uppercase tracking-[0.3em] text-ivory/90">
          {eyebrow}
        </motion.p>
        <motion.h1 {...enter(reduce, 0.3)} className="mt-5 max-w-4xl font-display text-[2.75rem] font-light leading-[1] text-ivory sm:text-6xl lg:text-7xl" data-testid="page-title">
          {title}
        </motion.h1>
        {sub && (
          <motion.p {...enter(reduce, 0.4)} className="mt-5 font-display text-2xl italic text-[#F6D2BC] [text-shadow:0_1px_14px_rgba(20,16,12,0.5)] sm:text-3xl">
            {sub}
          </motion.p>
        )}
        {intro && (
          <motion.p {...enter(reduce, 0.5)} className="mt-6 max-w-xl text-base leading-relaxed text-ivory/90 [text-shadow:0_1px_10px_rgba(20,16,12,0.45)] sm:text-lg">
            {intro}
          </motion.p>
        )}
        {children && (
          <motion.div {...enter(reduce, 0.65)} className="mt-9 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
            {children}
          </motion.div>
        )}
      </div>
    </section>
  );
};

export const SplitHero = ({ eyebrow, title, sub, intro, media, children, testid = "page-hero", tone = "ivory" }) => {
  const reduce = useReducedMotion();
  return (
    <section data-testid={testid} className={`relative overflow-hidden px-6 pb-20 pt-32 sm:px-8 lg:px-12 lg:pb-28 lg:pt-44 ${TONES[tone]}`}>
      <div aria-hidden className="pointer-events-none absolute -right-40 -top-20 h-[560px] w-[560px] rounded-full bg-[radial-gradient(circle,rgba(224,122,85,0.18),transparent_68%)]" />
      <div className="relative mx-auto grid max-w-7xl items-center gap-14 lg:grid-cols-12 lg:gap-12">
        <div className="lg:col-span-6">
          <motion.p {...enter(reduce, 0.1)} className="text-[11px] font-semibold uppercase tracking-[0.3em] text-coral-ink">
            {eyebrow}
          </motion.p>
          <motion.h1 {...enter(reduce, 0.2)} className="mt-5 font-display text-[2.75rem] font-light leading-[1] sm:text-6xl lg:text-7xl" data-testid="page-title">
            {title}
          </motion.h1>
          {sub && (
            <motion.p {...enter(reduce, 0.3)} className="mt-5 font-display text-2xl italic text-coral-ink sm:text-3xl">
              {sub}
            </motion.p>
          )}
          {intro && <motion.div {...enter(reduce, 0.4)} className="mt-6 max-w-xl space-y-4 text-base leading-relaxed text-ink-soft sm:text-lg">{intro}</motion.div>}
          {children && (
            <motion.div {...enter(reduce, 0.55)} className="mt-9 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
              {children}
            </motion.div>
          )}
        </div>
        <motion.div {...enter(reduce, 0.3)} className="lg:col-span-6">
          {media}
        </motion.div>
      </div>
    </section>
  );
};

export const Framed = ({ img, aspect = "aspect-[4/5]", sizes = "(min-width:1024px) 45vw, 92vw", eager = false, className = "", testid }) => (
  <div className={`overflow-hidden rounded-[2rem] bg-sand shadow-[0_40px_80px_-50px_rgba(43,38,32,0.6)] ${className}`}>
    <div className={aspect}>
      <Photo img={img} eager={eager} sizes={sizes} className="h-full w-full object-cover" data-testid={testid} />
    </div>
  </div>
);

export const Bullets = ({ items, dark = false, className = "" }) => (
  <ul className={`space-y-2.5 ${className}`}>
    {items.map((t) => (
      <li key={t} className={`flex gap-3 text-[15px] leading-relaxed ${dark ? "text-ivory/85" : "text-ink-soft"}`}>
        <span aria-hidden className="mt-2.5 h-1.5 w-1.5 shrink-0 rounded-full bg-coral" />
        {t}
      </li>
    ))}
  </ul>
);

export const Expandable = ({ label, children, testid, dark = false, defaultOpen = false, id }) => {
  const [open, setOpen] = useState(defaultOpen);
  const panelId = useId();
  useEffect(() => setOpen(defaultOpen), [defaultOpen]);
  return (
    <div id={id} className={`scroll-mt-28 border-t ${dark ? "border-ivory/15" : "border-ink/10"}`}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-controls={panelId}
        data-testid={testid}
        className={`group flex min-h-[56px] w-full items-center justify-between gap-4 py-3 text-left text-sm font-semibold uppercase tracking-[0.18em] ${dark ? "text-ivory" : "text-ink"}`}
      >
        {typeof label === "function" ? label(open) : label}
        <span aria-hidden className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full border transition-[transform,background-color,color] duration-500 ${open ? "rotate-45 border-coral-ink bg-coral-ink text-ivory" : dark ? "border-ivory/30" : "border-ink/20 group-hover:border-ink"}`}>
          <Plus className="h-4 w-4" strokeWidth={1.5} />
        </span>
      </button>
      <div id={panelId} inert={!open} className={`grid transition-[grid-template-rows,opacity] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] ${open ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}>
        <div className="overflow-hidden">
          <div className="pb-6">{children}</div>
        </div>
      </div>
    </div>
  );
};

export const SunPortrait = ({ img, testid }) => (
  <div className="relative mx-auto aspect-[4/5] w-full max-w-[300px] sm:max-w-[400px] lg:max-w-[520px]">
    <div aria-hidden className="absolute inset-x-[6%] bottom-0 top-[12%] rounded-t-full bg-[linear-gradient(to_bottom,#F2A07F,#EADFCF_70%)]" />
    <div aria-hidden className="absolute inset-x-[6%] bottom-0 top-[12%] rounded-t-full border border-coral/30" />
    <img
      src={src(img.p, 1000)}
      srcSet={srcSet(img.p, img.w)}
      sizes="(min-width:1024px) 520px, 90vw"
      alt={img.alt}
      fetchPriority="high"
      className="absolute bottom-0 left-1/2 h-[100%] w-auto max-w-none -translate-x-1/2 object-contain object-bottom"
      data-testid={testid}
    />
  </div>
);
